import React from "react";
import {
    Box, Text, IconButton, Tooltip, Divider, Spinner, Icon
} from "@chakra-ui/react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { AntSwitch } from "../../../stats/sharedCardStyles";

/**
 * Returns style props for the test mode toggle container.
 * @returns {Object} Chakra style props
 */
function testToggleContainerProps() {
    return {
        display: "flex", alignItems: "center", gap: 1.5,
        padding: "6px 10px", borderRadius: "6px",
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        transition: "all 0.3s ease",
        _hover: { background: "rgba(255, 255, 255, 0.08)", border: "1px solid rgba(255, 255, 255, 0.15)" },
        position: "relative"
    };
}

/**
 * Test mode toggle switch with loading indicator.
 * @param {object} props
 * @param {object} props.service - Service object with test flag
 * @param {boolean} props.loading - Whether the toggle is processing
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {function} props.onChange - Toggle change handler
 */
function TestModeToggle({ service, loading, isMobile, onChange }) {
    return (
        <Tooltip
            label={service?.test ? "Cette fiche est un test" : "Fiche en production. Veuillez basculer sur le mode Test"}
            hasArrow placement="top"
        >
            <Box {...testToggleContainerProps()}>
                {loading ? (
                    <Spinner boxSize="16px" color="#17dcb8" marginLeft="6px" />
                ) : (
                    <AntSwitch isChecked={service?.test} onChange={onChange} isDisabled={loading} aria-label="test mode" />
                )}
                <Text color="rgba(255, 255, 255, 0.9)" fontSize={isMobile ? "0.7rem" : "0.75rem"} fontWeight={600}>
                    Test
                </Text>
            </Box>
        </Tooltip>
    );
}

/**
 * Returns style props for the visibility toggle container based on hidden state.
 * @param {boolean} isHidden - Whether the service is hidden
 * @returns {Object} Chakra style props
 */
function visibilityContainerProps(isHidden) {
    return {
        display: "flex", alignItems: "center", gap: 1,
        padding: "6px 10px", borderRadius: "6px",
        background: isHidden ? "rgba(244, 67, 54, 0.08)" : "rgba(76, 175, 80, 0.08)",
        backdropFilter: "blur(10px)",
        border: isHidden ? "1px solid rgba(244, 67, 54, 0.2)" : "1px solid rgba(76, 175, 80, 0.2)",
        transition: "all 0.3s ease",
        _hover: {
            background: isHidden ? "rgba(244, 67, 54, 0.12)" : "rgba(76, 175, 80, 0.12)",
            border: isHidden ? "1px solid rgba(244, 67, 54, 0.3)" : "1px solid rgba(76, 175, 80, 0.3)"
        }
    };
}

/**
 * Renders the visibility icon or loading spinner.
 * @param {Object} props
 * @param {boolean} props.isHidden - Whether the service is hidden
 * @param {boolean} props.loading - Whether the toggle is processing
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function VisibilityIconContent({ isHidden, loading, isMobile }) {
    if (loading) {
        return <Spinner boxSize={isMobile ? "16px" : "18px"} color="inherit" />;
    }
    return <Icon as={isHidden ? MdVisibilityOff : MdVisibility} boxSize={isMobile ? "16px" : "18px"} />;
}

/**
 * Visibility toggle button with color-coded state (green/red).
 * @param {object} props
 * @param {object} props.service - Service object with hidden flag
 * @param {boolean} props.loading - Whether the toggle is processing
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {function} props.onChange - Toggle change handler
 */
function VisibilityToggle({ service, loading, isMobile, onChange }) {
    const isHidden = service?.hidden;
    const color = isHidden ? "rgba(244, 67, 54, 0.9)" : "rgba(76, 175, 80, 0.9)";

    return (
        <Tooltip
            label={isHidden ? "Cette fiche est non visible" : "Cette fiche est visible pour tous"}
            hasArrow placement="top"
        >
            <Box {...visibilityContainerProps(isHidden)}>
                <IconButton
                    aria-label="Changer la visibilite" size="sm" variant="ghost"
                    onClick={onChange} isDisabled={loading}
                    color={color} padding="4px" minW="auto" h="auto"
                    _hover={{ background: "transparent" }}
                    _disabled={{ color: "rgba(255, 255, 255, 0.3)" }}
                    icon={<VisibilityIconContent isHidden={isHidden} loading={loading} isMobile={isMobile} />}
                />
                {!isMobile && (
                    <Text color={color} fontSize="0.7rem" fontWeight={600}>
                        {isHidden ? "Caché" : "Visible"}
                    </Text>
                )}
            </Box>
        </Tooltip>
    );
}

/**
 * Glass-styled action bar with test mode toggle and visibility controls.
 * @param {object} props
 * @param {object} props.service - Service object
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {boolean} props.loadingTest - Whether test toggle is processing
 * @param {boolean} props.loadingHidden - Whether visibility toggle is processing
 * @param {function} props.onToggleTest - Handler for test mode toggle
 * @param {function} props.onToggleHidden - Handler for visibility toggle
 */
function ServiceActionBar({ service, isMobile, loadingTest, loadingHidden, onToggleTest, onToggleHidden }) {
    return (
        <Box display="flex" alignItems="center" gap={isMobile ? 2 : 3} flexWrap="wrap"
            padding={isMobile ? "10px" : "12px"}
            background="rgba(255, 255, 255, 0.08)" backdropFilter="blur(10px)"
            borderRadius="8px" border="1px solid rgba(255, 255, 255, 0.12)"
            marginBottom={isMobile ? 3 : 4}>
            <TestModeToggle service={service} loading={loadingTest} isMobile={isMobile} onChange={onToggleTest} />
            <Divider orientation="vertical" height="24px" borderColor="rgba(255, 255, 255, 0.15)" mx={1} />
            <VisibilityToggle service={service} loading={loadingHidden} isMobile={isMobile} onChange={onToggleHidden} />
        </Box>
    );
}

export default ServiceActionBar;
